import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import {
  Typography,
  Autocomplete,
  TextField,
  Box,
  Button,
} from "@mui/material";
import { getUrl } from "src/Utils/Common";
import PrimaryButton from "src/Components/shared/shared/PrimaryButton";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import useRefreshData from "src/Utils/CustomHooks/useRefreshData";
import { theme } from "../../../Theme/index";
import { useSelector } from "react-redux";

function PriceElasticity({
  selectedSkus: propSelectedSkus,
  setSelectedSkus: propSetSelectedSkus,
}) {
  const [chartSeries, setChartSeries] = useState([]); // Holds chart data for multiple SKUs
  const [xAxisCategories, setXAxisCategories] = useState([]); // Holds the x-axis categories
  const [skus, setSkus] = useState([]); // List of available SKUs
  const [selectedSkus, setSelectedSkus] = useState(propSelectedSkus || []);
  const [loading, setLoading] = useState(false); 
  const [error, setError] = useState(null); // Error state
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false); 

  const projectId = useSelector((state) => state.userProject.projectId);
  const projectName = projectId != null ? String(projectId) : null;
  const simRequestHeaders = {
    "Content-Type": "application/json",
    "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
    "x-access-token": localStorage.getItem("userId") || "",
  };

  const currentSelectedSkus =
    propSelectedSkus !== undefined ? propSelectedSkus : selectedSkus;
  const setCurrentSelectedSkus =
    propSetSelectedSkus !== undefined ? propSetSelectedSkus : setSelectedSkus;

  // Fetch SKUs from the server
  useEffect(() => {
    async function fetchSkus() {
      if (!projectName) return;
      try {
        const response = await fetch(
          `${getUrl()}/sim/get_sku_by_project_name?project_name=${encodeURIComponent(projectName)}`,
          {
            method: "GET",
            headers: simRequestHeaders,
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const skusData = await response.json();
        setSkus(skusData.skus || []);
      } catch (err) {
        setError(err.message);
      }
    }

    fetchSkus();
  }, [projectName]);

  // Fetch Elasticity Data for selected SKUs
  const fetchElasticityData = async () => {
    if (!projectName) {
      setError("Project not selected.");
      return;
    }
    if (!currentSelectedSkus.length) {
      setError("Please select at least one SKU.");
      return;
    }

    try {
      setLoading(true);
      setError(null); 
      setHasAttemptedFetch(true);

      const response = await fetch(`${getUrl()}/sim/elasticity_chart`, {
        method: "POST",
        headers: simRequestHeaders,
        body: JSON.stringify({
          project_name: projectName,
          skus_array: currentSelectedSkus,
          sku: 5,
          start: -0.5,
          end: 0.5,
          step: 0.1,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data || Object.keys(data).length === 0 || !data[currentSelectedSkus[0]] || Object.keys(data[currentSelectedSkus[0]]).length === 0) {
        setError("No elasticity data available for the selected SKUs. Please try different SKUs or check your data source.");
        setChartSeries([]);
        setXAxisCategories([]);
        setLoading(false);
        return;
      }

      // Process the data for chart
      const categories = Object.keys(data[currentSelectedSkus[0]] || {}).map(
        (key) => parseFloat(key).toFixed(1)
      );
      setXAxisCategories(categories);

      const series = currentSelectedSkus.map((sku) => ({
        name: sku,
        data: Object.values(data[sku] || {}).map((value) => parseFloat(value)),
      }));

      setChartSeries(series);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Use the refresh hook
  const { handleRefresh, isRefreshing } = useRefreshData(fetchElasticityData);

  const chartOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    xaxis: {
      categories: xAxisCategories,
      title: {
        text: "Price Elasticity",
        style: { fontSize: "14px", fontWeight: "bold" },
      },
    },
    yaxis: {
      title: {
        text: "% Change in Volume Share",
        style: { fontSize: "14px", fontWeight: "bold" },
      },
      labels: {
        rotate: -45, // ✅ Rotates labels for better spacing
        style: {
          fontSize: "10px", // ✅ Reduces font size on mobile
        },
        formatter: function (val) {
          return val.toFixed(0);
        },
      },
    },
    markers: { size: 5 },
    legend: { position: "top" },
    tooltip: { shared: true, intersect: false },
    colors: ["#08306B", "#CD393B", "#0070c0", "#423f38"], // Colors for multiple SKUs
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1200px",
        margin: "auto",
        overflowX: "hidden",
      }}
    >
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: {
            xs: "0.9rem",
            sm: "1.2rem",
            md: "1.3rem",
          },
          px: {
            xs: 0.5,
            sm: 0.75,
            md: 1,
          },
          color: theme.palette.primary.main,
          textAlign: "center",
          m: 0,
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 48,
        }}
      >
        Price Elasticity
      </Typography>
      <Box
        sx={{
          width: "100%",
          maxWidth: "600px",
          margin: "auto",
          textAlign: "center",
        }}
      >
        <Autocomplete
          multiple
          options={skus}
          value={currentSelectedSkus}
          onChange={(event, newValue) => {
            setCurrentSelectedSkus(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select SKUs"
              variant="outlined"
              size="small"
              placeholder="Search or select SKU"
              sx={{
                width: "100%",
                maxWidth: 300, // Ensure dropdown doesn't shrink too much
                "@media (max-width: 600px)": {
                  width: "100%", // Full width on mobile
                  fontSize: "10px",
                },
              }}
            />
          )}
          sx={{
            width: 300,
            marginBottom: 2,
            ml: 3,
            "@media (max-width: 600px)": {
              width: "100%", // Make sure it stretches properly on mobile
              ml: 0, // Remove left margin on small screens
            },
          }}
        />

        <PrimaryButton
          onClick={fetchElasticityData}
          sx={{
            marginBottom: { xs: 2, sm: 3 },
            width: "100%",
            maxWidth: { xs: "150px", sm: "200px" },
            display: "block",
            mx: "auto",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            padding: { xs: "6px 16px", sm: "8px 22px" },
          }}
        >
          Fetch Data
        </PrimaryButton>
      </Box>

      {loading || isRefreshing ? (
        <FacebookCircularProgress />
      ) : error ? (
        <FacebookCircularProgress
          loading={false}
          hasData={false}
          onRefresh={handleRefresh}
          message={error}
        />
      ) : chartSeries.length > 0 ? (
        <ReactApexChart
          options={chartOptions}
          series={chartSeries}
          type="line"
          width={window.innerWidth < 600 ? "100%" : 500}
          height={window.innerWidth < 600 ? 200 : 260}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            maxWidth: "800px",
            margin: "auto",
            textAlign: "center",
            py: 4,
          }}
        >
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {hasAttemptedFetch
              ? "No data available for the selected SKUs. Please try different SKUs or refresh."
              : "Select SKUs and click Fetch Data to view price elasticity chart"}
          </Typography>
          {hasAttemptedFetch && (
            <Box sx={{ mt: 2 }}>
              <FacebookCircularProgress
                loading={false}
                hasData={false}
                onRefresh={handleRefresh}
                message="Click to refresh with current SKUs"
              />
            </Box>
          )}
        </Box>
      )}

      {/* <Box width="60%">
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  style={{
                    fontWeight: "bold",
                    fontSize: "12px",
                    padding: "4px 8px",
                  }}
                >
                  Price Elasticity
                </TableCell>
                <TableCell
                  align="center"
                  style={{
                    fontWeight: "bold",
                    fontSize: "12px",
                    padding: "4px 8px",
                  }}
                >
                  % Change in Volume Share
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData.map((row, index) => (
                <TableRow key={index} sx={{ height: "24px" }}>
                  <TableCell
                    sx={{
                      padding: "4px 8px",
                      fontSize: "12px",
                    }}
                  >
                    {row.elasticity}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      padding: "4px 8px",
                      fontSize: "12px",
                    }}
                  >
                    {row.value.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box> */}
    </Box>
  );
}

export default PriceElasticity;
